import React, { createContext, useContext, useMemo } from "react";
import {
  Toast,
  ToastTitle,
  Button,
  ToastTrigger,
  Toaster,
  Caption1,
  Caption1Strong,
  useToastController,
  makeStyles
} from "@fluentui/react-components"; // Adjust imports as needed
import { Dismiss20Regular } from "@fluentui/react-icons";
import PropTypes from "prop-types";
import { useSidebar } from "./SidebarContext";
import useMediaQuery from "../hooks/useMediaQuery";

// Create the Notification Context
const NotificationContext = createContext();

const useStyles = makeStyles({
  toast: {
    zIndex: 1000,
    borderRadius: "4px",
    border: "1px solid rgba(159, 216, 159, 1)",
    background: "var(--secondaryColorBackground)",
    "& .fui-ToastTitle__media": {
      alignItems: "center"
    }
  },
  toaster: {
    width: "100%"
  }
});

// Notification Provider Component
export const NotificationProvider = ({ children }) => {
  const toasterId = "global-toaster"; // Unique ID for the toaster
  const { dispatchToast, dismissToast, dismissAllToasts } = useToastController(toasterId);
  const styles = useStyles();

  const { isSidebarOpen, isInFullscreen, isSidebarAvailable } = useSidebar();
  const showNotificationFullScreen = useMemo(() => {
    // If no sidebar provider/consumer is mounted, treat it as not available (full width)
    const sidebarPresent = isSidebarAvailable;
    return isInFullscreen || !sidebarPresent || !isSidebarOpen;
  }, [isInFullscreen, isSidebarOpen, isSidebarAvailable]);

  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Centralized notify function
  const notify = ({
    actionLabel,
    action,
    captionTitle,
    caption,
    intent = "success",
    timeout = 3000,
    position = "bottom-start"
  }) => {
    dispatchToast(
      <Toast
        className={styles.toast}
        style={{
          width: isDesktop && !showNotificationFullScreen ? "calc(100vw - 360px)" : "calc(100vw - 120px)"
        }}
      >
        <ToastTitle
          style={{ alignItems: "center" }}
          action={
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {action && (
                <Button onClick={action} appearance="primary">
                  {actionLabel}
                </Button>
              )}
              <ToastTrigger>
                <Button className={styles.closeIcon} appearance="transparent" icon={<Dismiss20Regular />} />
              </ToastTrigger>
            </div>
          }
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <Caption1>
              {captionTitle && <Caption1Strong style={{ marginRight: "8px" }}>{captionTitle}</Caption1Strong>}
              {caption && <>{caption}</>}
            </Caption1>
          </div>
        </ToastTitle>
      </Toast>,
      { intent, timeout, position }
    );
  };

  const offset = useMemo(() => {
    if (isDesktop && !showNotificationFullScreen) {
      return { horizontal: 340 };
    }
    return { horizontal: 72 };
  }, [isDesktop, showNotificationFullScreen]);

  window.notify = notify;

  return (
    <NotificationContext.Provider value={{ notify, dismissToast, dismissAllToasts }}>
      {children}
      <Toaster offset={offset} toasterId={toasterId} className={styles.toaster} />
    </NotificationContext.Provider>
  );
};

// Custom Hook to use the Notification Context
export const useNotifier = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifier must be used within a NotificationProvider");
  }
  return context;
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired
};
